import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withValue } from '@g4/automation-hooks';
import styled from 'styled-components';
import tracking from '@g4/g4-analytics';

import { Box, Text, VerticalSpacing, useMedia } from '../components/Boneyard';
import getDestinationAdvertTrackingdata, {
  getAffinityString,
} from '../pageComponents/app/trackingItems';
import useFlightSearchCriteria from '../hooks/useFlightSearchCriteria';
import { useBookingPath, useCart } from '../graphql';

const propTypes = {
  automationHook: PropTypes.string,
  children: PropTypes.node,
  advertImageDesktop: PropTypes.string,
  advertImageResponsive: PropTypes.string,
  alt: PropTypes.string,
  advertName: PropTypes.string,
  positionGAValue: PropTypes.string,
  link: PropTypes.string,
  className: PropTypes.string,
};

const Wrapper = styled.div`
  text-align: center;
  ${props => props.theme.query.md} {
    text-align: left;
  }
`;

const AdvertImg = styled.img`
  width: 100%;
  height: auto;
  max-width: ${props => (props.maxWidth > 0 ? `${props.maxWidth}px` : 'none')};
  ${props => props.theme.query.md} {
    max-width: none;
  }
`;

const DestinationAdvert = ({
  children,
  className,
  automationHook,
  advertImageDesktop,
  advertImageResponsive,
  alt,
  advertName,
  positionGAValue,
  link,
}) => {
  const [responsiveImgObject, setResponsiveImgObject] = useState(null);
  const mq = useMedia();
  const isMobile = mq.lt('md');
  const hasPromoViewTracked = useRef(false);

  const haveImage = advertImageResponsive || advertImageDesktop;

  const cta = children?.[0]?.props?.children?.[0];
  const { operatedBy } = useBookingPath();
  const { data: searchCriteria } = useFlightSearchCriteria();
  const route = searchCriteria?.origin + searchCriteria?.destination;
  const { cart } = useCart();
  const affinityString = getAffinityString(cart?.order);

  useEffect(() => {
    const img = new Image();
    img.src = isMobile ? advertImageResponsive : advertImageDesktop;
    setResponsiveImgObject(img);
  }, [isMobile, advertImageResponsive, advertImageDesktop]);

  const impressionData = React.useMemo(() => {
    const promoData = {
      advertImageDesktop,
      advertImageResponsive,
      advertName,
      haveImage,
      isMobile,
      positionGAValue,
      cta,
      alt,
      route,
      operatedBy,
      affinityString,
    };
    return getDestinationAdvertTrackingdata(promoData);
  }, [
    advertImageDesktop,
    advertImageResponsive,
    advertName,
    haveImage,
    isMobile,
    positionGAValue,
    cta,
    alt,
    route,
    operatedBy,
    affinityString,
  ]);

  useEffect(() => {
    if (searchCriteria && !hasPromoViewTracked.current) {
      tracking.trackPromoView(impressionData);
      hasPromoViewTracked.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCriteria, impressionData]);

  const trackPromoClick = () => {
    tracking.trackPromoSelect(impressionData);
  };

  return (
    <>
      <Box>
        <Wrapper>
          {haveImage && link && (
            <a href={link} target="_blank" rel="noopener noreferrer">
              <AdvertImg
                src={responsiveImgObject?.src}
                maxWidth={responsiveImgObject?.naturalWidth}
                alt={alt}
              />
            </a>
          )}
          {haveImage && !link && searchCriteria && (
            <AdvertImg
              src={responsiveImgObject?.src}
              maxWidth={responsiveImgObject?.naturalWidth}
              alt={alt}
              onClick={trackPromoClick}
            />
          )}
        </Wrapper>
      </Box>
      <VerticalSpacing size={2} />
      <Box
        d={className ? 'flex' : 'block'}
        direction={'row'}
        pl={className ? 3 : 0}
      >
        <Text
          size={isMobile ? 12 : 16}
          weight={400}
          lh={isMobile ? 1.6 : 1.4}
          automationHook={withValue(automationHook, 'banner-description')}
        >
          {children}
        </Text>
      </Box>
      <VerticalSpacing size={2} />
    </>
  );
};

DestinationAdvert.propTypes = propTypes;

export default DestinationAdvert;
